/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import {
    Button, Input, Modal, message, Tag, Select, DatePicker, Space,
    Popconfirm, Tooltip, Row, Col
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
    ExclamationCircleOutlined, FileTextOutlined,
    SearchOutlined, WarningOutlined, DashboardOutlined
} from '@ant-design/icons';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer, LabelList
} from 'recharts';
import moment from 'moment';
import FleetService from '../../services/FleetService';

const { TextArea } = Input;
const { Option }   = Select;

// ─── SHARED: Period Filter Component ────────────────────────────────────────
// Drop this into any file that needs time period filtering
// Usage: <PeriodFilter value={period} onChange={setPeriod} />
// period = { preset: 'this_month'|'last_3m'|'this_year'|'last_year'|'custom'|'all', from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
// Filter data: filterByPeriod(data, period, dateField)

const { RangePicker } = DatePicker;

const PRESETS = [
    { key: 'this_month', label: 'This Month',   from: () => moment().startOf('month'),   to: () => moment() },
    { key: 'last_3m',    label: 'Last 3 Months',from: () => moment().subtract(3,'months'),to:() => moment() },
    { key: 'this_year',  label: 'This Year',     from: () => moment().startOf('year'),    to: () => moment() },
    { key: 'last_year',  label: 'Last Year',     from: () => moment().subtract(1,'year').startOf('year'), to: () => moment().subtract(1,'year').endOf('year') },
    { key: 'all',        label: 'All Time',      from: () => null, to: () => null },
];

const DEFAULT_PERIOD = { preset: 'this_year', from: moment().startOf('year').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') };

function PeriodFilter({ value, onChange, style }) {
    const cur = value || DEFAULT_PERIOD;
    const handlePreset = (preset) => {
        const p = PRESETS.find(x => x.key === preset);
        onChange({
            preset,
            from: p.from() ? p.from().format('YYYY-MM-DD') : '',
            to:   p.to()   ? p.to().format('YYYY-MM-DD')   : '',
        });
    };
    const handleRange = ([s, e]) => {
        onChange({
            preset: 'custom',
            from: s ? s.format('YYYY-MM-DD') : '',
            to:   e ? e.format('YYYY-MM-DD') : '',
        });
    };
    return (
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', ...style }}>
            <span style={{ fontSize:11, color:'#888', whiteSpace:'nowrap' }}>Period:</span>
            {PRESETS.map(p => (
                <button key={p.key} onClick={() => handlePreset(p.key)} style={{
                    padding:'3px 10px', border:'1px solid', borderRadius:12, cursor:'pointer',
                    fontSize:11, fontWeight: cur.preset===p.key ? 600 : 400,
                    background: cur.preset===p.key ? '#1a3a5c' : '#fff',
                    borderColor: cur.preset===p.key ? '#1a3a5c' : '#d9d9d9',
                    color: cur.preset===p.key ? '#fff' : '#595959',
                }}>{p.label}</button>
            ))}
            <RangePicker size="small" style={{ width:210 }}
                value={cur.preset==='custom' && cur.from && cur.to
                    ? [moment(cur.from), moment(cur.to)] : null}
                onChange={handleRange}
                format="DD MMM YYYY"
                placeholder={['Custom from','Custom to']}
            />
            {cur.from && cur.to && (
                <span style={{ fontSize:10, color:'#1a5b9a', background:'#e6f4ff',
                    padding:'2px 8px', borderRadius:8, whiteSpace:'nowrap' }}>
                    {moment(cur.from).format('DD MMM YYYY')} → {moment(cur.to).format('DD MMM YYYY')}
                </span>
            )}
        </div>
    );
}

function filterByPeriod(data, period, dateField) {
    if (!period || period.preset === 'all' || (!period.from && !period.to)) return data;
    return data.filter(r => {
        const d = r[dateField];
        if (!d || d === '0000-00-00') return false;
        const m = moment(d);
        if (period.from && m.isBefore(moment(period.from), 'day')) return false;
        if (period.to   && m.isAfter(moment(period.to),   'day')) return false;
        return true;
    });
}

const INCIDENT_TYPES  = ['Accident (Fault)','Accident (No Fault)','Theft','Vandalism','Breakdown','Near-Miss','Fire','Flood Damage','Other'];
const SEVERITIES      = ['Low','Medium','High','Critical'];
const CLAIM_STATUSES  = ['Not Applicable','Pending','Submitted','Approved','Rejected','Settled'];
const REGIONS         = ['Hhohho','Manzini','Shiselweni','Lubombo','National'];

const SEV_C = {
    Low:      { bg:'#f6ffed', border:'#b7eb8f', text:'#389e0d' },
    Medium:   { bg:'#fffbe6', border:'#ffe58f', text:'#d48806' },
    High:     { bg:'#fff2e8', border:'#ffbb96', text:'#d4380d' },
    Critical: { bg:'#fff0f6', border:'#ffadd2', text:'#c41d7f' },
};
const STATUS_C  = { Open:'orange', Resolved:'green', 'Under Review':'blue' };
const PIE_C     = ['#f5222d','#faad14','#52c41a','#1890ff','#722ed1','#13c2c2'];

const EMPTY_FORM = {
    id:null,vehicle_id:null,driver_id:null,incident_date:null,incident_time:'',
    location:'',region:'',assigned_programme:'',incident_type:'',severity:'Medium',
    description:'',third_party:0,police_report_no:'',injuries_reported:'',
    damage_assessment:'',repair_cost:'',insurance_claim_no:'',claim_status:'Not Applicable',
    action_taken:'',date_resolved:null,reported_by:null,approved_by:null,notes:'',
};

const fmt    = (v) => v || '—';
const fmtD   = (v) => v ? moment(v).format('DD MMM YYYY') : '—';
const toN    = (v) => Number(v || 0);

// ─── Anomaly detector ────────────────────────────────────────────────────────
function detectIncidentAnomalies(incidents) {
    const issues = [];
    // Same vehicle multiple open incidents
    const openByVeh = {};
    incidents.filter(i => i.status === 'Open').forEach(i => {
        openByVeh[i.vehicle_id] = (openByVeh[i.vehicle_id] || []).concat(i);
    });
    Object.entries(openByVeh).forEach(([, list]) => {
        if (list.length > 1) {
            issues.push({ type:'error', msg:`${list[0].registration_no} has ${list.length} open incidents simultaneously` });
        }
    });
    // Unresolved > 30 days
    incidents.filter(i => i.status === 'Open').forEach(i => {
        const days = moment().diff(moment(i.incident_date), 'days');
        if (days > 30) issues.push({ type:'warn', msg:`${i.registration_no} — incident open for ${days} days (${fmtD(i.incident_date)})` });
    });
    // High/Critical with no action taken
    incidents.filter(i => ['High','Critical'].includes(i.severity) && !i.action_taken).forEach(i => {
        issues.push({ type:'error', msg:`${i.registration_no} — ${i.severity} severity with no action recorded` });
    });
    // Pending insurance claims > 60 days
    incidents.filter(i => i.claim_status === 'Pending').forEach(i => {
        const days = moment().diff(moment(i.incident_date), 'days');
        if (days > 60) issues.push({ type:'warn', msg:`${i.registration_no} — insurance claim pending ${days} days` });
    });
    return issues;
}

// ─── Mini Dashboard ───────────────────────────────────────────────────────────
function IncidentDashboard({ incidents, period, onPeriodChange }) {
    const byType = useMemo(() => {
        const m = {};
        incidents.forEach(i => { m[i.incident_type] = (m[i.incident_type]||0)+1; });
        return Object.entries(m).map(([name,value]) => ({name,value})).sort((a,b)=>b.value-a.value);
    }, [incidents]);

    const bySeverity = useMemo(() => {
        const m = {};
        incidents.forEach(i => { m[i.severity||'Unknown'] = (m[i.severity||'Unknown']||0)+1; });
        return Object.entries(m).map(([name,value]) => ({name,value}));
    }, [incidents]);

    const byMonth = useMemo(() => {
        const m = {};
        incidents.forEach(i => {
            const mo = moment(i.incident_date).format('MMM YY');
            m[mo] = (m[mo]||0)+1;
        });
        return Object.entries(m).slice(-8).map(([month,count]) => ({month,count}));
    }, [incidents]);

    const byStatus = useMemo(() => {
        const m = {};
        incidents.forEach(i => { m[i.status||'Open'] = (m[i.status||'Open']||0)+1; });
        return Object.entries(m).map(([name,value]) => ({name,value}));
    }, [incidents]);

    const totalCost = incidents.reduce((s,i) => s + toN(i.repair_cost), 0);
    const openRate  = incidents.length ? ((incidents.filter(i=>i.status==='Open').length/incidents.length)*100).toFixed(0) : 0;
    const avgDaysOpen = useMemo(() => {
        const open = incidents.filter(i => i.status === 'Open');
        if (!open.length) return null;
        return (open.reduce((s,i) => s + moment().diff(moment(i.incident_date),'days'), 0) / open.length).toFixed(0);
    }, [incidents]);

    const anomalies = useMemo(() => detectIncidentAnomalies(incidents), [incidents]);

    return (
        <div style={{ background:'#fafafa', border:'1px solid #e8e8e8', borderRadius:8, padding:'14px 16px', marginBottom:20 }}>
            {/* Period filter */}
            <PeriodFilter value={period} onChange={onPeriodChange} style={{ marginBottom:12 }} />
            {incidents.length === 0 && (
                <div style={{ textAlign:'center', padding:20, color:'#aaa', fontSize:12 }}>
                    No incidents in selected period
                </div>
            )}

            {/* Anomaly banner */}
            {anomalies.length > 0 && (
                <div style={{ background:'#fff1f0', border:'1px solid #ffa39e', borderRadius:6,
                    padding:'8px 14px', marginBottom:14 }}>
                    <div style={{ fontWeight:700, color:'#cf1322', fontSize:12, marginBottom:6 }}>
                        <WarningOutlined style={{ marginRight:6 }} />
                        {anomalies.length} irregularit{anomalies.length>1?'ies':'y'} detected
                    </div>
                    {anomalies.map((a,i) => (
                        <div key={i} style={{ fontSize:11, display:'flex', gap:6, marginBottom:3 }}>
                            <Tag color={a.type==='error'?'red':'orange'} style={{ fontSize:9 }}>
                                {a.type==='error'?'Error':'Warning'}
                            </Tag>
                            {a.msg}
                        </div>
                    ))}
                </div>
            )}

            {/* Insight pills */}
            <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
                {[
                    { label:'Open Rate',        value:`${openRate}%`,   color: Number(openRate)>40?'#cf1322':'#389e0d' },
                    { label:'Avg Days Open',     value: avgDaysOpen ? `${avgDaysOpen}d` : '—', color: Number(avgDaysOpen)>30?'#cf1322':'#595959' },
                    { label:'Est. Repair Cost',  value:`E ${totalCost.toLocaleString()}`, color:'#d46b08' },
                    { label:'High/Critical',     value: incidents.filter(i=>['High','Critical'].includes(i.severity)).length, color:'#c41d7f' },
                    { label:'Pending Claims',    value: incidents.filter(i=>i.claim_status==='Pending').length, color:'#1890ff' },
                ].map(({label,value,color}) => (
                    <div key={label} style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6,
                        padding:'6px 12px', borderLeft:`3px solid ${color}` }}>
                        <div style={{ fontSize:10, color:'#888' }}>{label}</div>
                        <div style={{ fontSize:16, fontWeight:700, color }}>{value}</div>
                    </div>
                ))}
            </div>

            {/* Charts row */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1.2fr 1fr 1fr', gap:12 }}>
                {/* Status pie */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>By Status</div>
                    <ResponsiveContainer width="100%" height={130}>
                        <PieChart>
                            <Pie data={byStatus} dataKey="value" nameKey="name"
                                cx="50%" cy="50%" outerRadius={50} labelLine={false}>
                                {byStatus.map((d,i) => (
                                    <Cell key={i} fill={d.name==='Open'?'#faad14':d.name==='Resolved'?'#52c41a':'#1890ff'} />
                                ))}
                            </Pie>
                            <RTooltip />
                            <Legend iconSize={8} wrapperStyle={{ fontSize:9 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Type bar */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>By Incident Type</div>
                    <ResponsiveContainer width="100%" height={130}>
                        <BarChart data={byType.slice(0,5)} layout="vertical"
                            margin={{ top:0, right:30, left:90, bottom:0 }}>
                            <XAxis type="number" tick={{ fontSize:9 }} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize:9 }} width={85} />
                            <RTooltip />
                            <Bar dataKey="value" fill="#f5222d" radius={[0,3,3,0]}>
                                <LabelList dataKey="value" position="right" style={{ fontSize:9 }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Severity pie */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>By Severity</div>
                    <ResponsiveContainer width="100%" height={130}>
                        <PieChart>
                            <Pie data={bySeverity} dataKey="value" nameKey="name"
                                cx="50%" cy="50%" outerRadius={50} labelLine={false}>
                                {bySeverity.map((d,i) => (
                                    <Cell key={i} fill={
                                        d.name==='Critical'?'#c41d7f':d.name==='High'?'#f5222d':
                                        d.name==='Medium'?'#faad14':'#52c41a'} />
                                ))}
                            </Pie>
                            <RTooltip />
                            <Legend iconSize={8} wrapperStyle={{ fontSize:9 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Monthly trend */}
                <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:6, padding:'10px' }}>
                    <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:'#1a3a5c' }}>Monthly Trend</div>
                    <ResponsiveContainer width="100%" height={130}>
                        <BarChart data={byMonth} margin={{ top:0, right:5, left:-20, bottom:0 }}>
                            <XAxis dataKey="month" tick={{ fontSize:8 }} />
                            <YAxis tick={{ fontSize:8 }} allowDecimals={false} />
                            <RTooltip />
                            <Bar dataKey="count" fill="#1890ff" radius={[3,3,0,0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

// ─── Incident Form Modal ──────────────────────────────────────────────────────
function IncidentFormModal({ open, incident, vehicles, drivers, onSaved, onClose }) {
    const [form,setForm]    = useState(EMPTY_FORM);
    const [saving,setSaving]= useState(false);

    useEffect(() => {
        if (open) setForm(incident ? { ...EMPTY_FORM,...incident } : { ...EMPTY_FORM });
    }, [open, incident?.id]);

    const set = (k,v) => setForm(p => ({...p,[k]:v}));

    const handleSave = async () => {
        if (!form.vehicle_id)    { message.error('Select a vehicle'); return; }
        if (!form.incident_date) { message.error('Enter the incident date'); return; }
        if (!form.incident_type) { message.error('Select incident type'); return; }
        if (!form.description)   { message.error('Enter a description'); return; }
        setSaving(true);
        try {
            const payload = {
                ...form,
                incident_date: form.incident_date ? moment(form.incident_date).format('YYYY-MM-DD') : null,
                date_resolved: form.date_resolved  ? moment(form.date_resolved).format('YYYY-MM-DD')  : null,
                repair_cost:   form.repair_cost || null,
                third_party:   form.third_party ? 1 : 0,
            };
            const res = await FleetService.saveIncident(payload);
            if (Number(res?.data?.code) === 200) { message.success(res.data.message||'Saved'); onSaved(); }
            else message.error(res?.data?.message||'Failed');
        } catch(e) { message.error(e.message||'Error'); }
        finally { setSaving(false); }
    };

    const lbl  = { fontSize:12, color:'#666', marginBottom:4 };
    const fbox = { marginBottom:14 };

    return (
        <Modal open={open} onCancel={onClose} width={760} footer={null} destroyOnClose
            title={
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <ExclamationCircleOutlined style={{ color:'#d4380d' }} />
                    <span>{form.id ? 'Edit Incident' : 'Log New Incident'}</span>
                    {form.id && <Tag color={STATUS_C[form.status]||'default'}>{form.status}</Tag>}
                </div>
            }>
            {/* Vehicle & Driver */}
            <div style={{ background:'#f0f5ff', border:'1px solid #adc6ff', borderRadius:6, padding:'14px 16px', marginBottom:16 }}>
                <div style={{ fontWeight:600, color:'#2f54eb', marginBottom:12 }}>🚗 Vehicle & Driver</div>
                <Row gutter={16}>
                    <Col span={12}>
                        <div style={fbox}>
                            <div style={lbl}>Vehicle <span style={{ color:'red' }}>*</span></div>
                            <Select showSearch style={{ width:'100%' }} value={form.vehicle_id}
                                onChange={v => {
                                    set('vehicle_id', v);
                                    const veh = vehicles.find(x => x.id === v);
                                    if (veh) {
                                        if (veh.region)             set('region', veh.region);
                                        if (veh.assigned_programme) set('assigned_programme', veh.assigned_programme);
                                    }
                                }}
                                placeholder="Select vehicle" optionFilterProp="children">
                                {vehicles.map(v => (
                                    <Option key={v.id} value={v.id}>
                                        {v.registration_no} — {v.vehicle_type}{v.facility ? ` (${v.facility})` : ''}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={fbox}>
                            <div style={lbl}>Driver (if applicable)</div>
                            <Select showSearch style={{ width:'100%' }} value={form.driver_id}
                                onChange={v => set('driver_id',v)} placeholder="Select driver" allowClear optionFilterProp="children">
                                {drivers.map(d => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                            </Select>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Incident Details */}
            <div style={{ background:'#fff2e8', border:'1px solid #ffbb96', borderRadius:6, padding:'14px 16px', marginBottom:16 }}>
                <div style={{ fontWeight:600, color:'#d4380d', marginBottom:12 }}>📋 Incident Details</div>
                <Row gutter={16}>
                    <Col span={8}><div style={fbox}><div style={lbl}>Date <span style={{ color:'red' }}>*</span></div>
                        <DatePicker style={{ width:'100%' }}
                            value={form.incident_date ? moment(form.incident_date) : null}
                            onChange={d => set('incident_date', d ? d.format('YYYY-MM-DD') : null)}
                            format="DD MMM YYYY" disabledDate={d => d && d.isAfter(moment())} />
                    </div></Col>
                    <Col span={8}><div style={fbox}><div style={lbl}>Time</div>
                        <Input value={form.incident_time} onChange={e => set('incident_time',e.target.value)} placeholder="e.g. 14:30" />
                    </div></Col>
                    <Col span={8}><div style={fbox}><div style={lbl}>Severity</div>
                        <Select style={{ width:'100%' }} value={form.severity} onChange={v => set('severity',v)}>
                            {SEVERITIES.map(s => <Option key={s} value={s}><span style={{ color:SEV_C[s]?.text }}>● {s}</span></Option>)}
                        </Select>
                    </div></Col>
                    <Col span={12}><div style={fbox}><div style={lbl}>Incident Type <span style={{ color:'red' }}>*</span></div>
                        <Select style={{ width:'100%' }} value={form.incident_type} onChange={v => set('incident_type',v)} placeholder="Select type">
                            {INCIDENT_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                        </Select>
                    </div></Col>
                    <Col span={6}><div style={fbox}><div style={lbl}>Region</div>
                        <Select style={{ width:'100%' }} value={form.region} onChange={v => set('region',v)} allowClear>
                            {REGIONS.map(r => <Option key={r} value={r}>{r}</Option>)}
                        </Select>
                    </div></Col>
                    <Col span={6}><div style={fbox}>
                        <div style={lbl}>Programme <span style={{ fontSize:10, color:'#1677ff' }}>auto-filled</span></div>
                        <Input value={form.assigned_programme||''} onChange={e => set('assigned_programme',e.target.value)}
                            placeholder="Auto-fills on vehicle select"
                            style={{ background: form.assigned_programme ? '#f6ffed' : undefined }} />
                    </div></Col>
                    <Col span={24}><div style={fbox}><div style={lbl}>Location</div>
                        <Input value={form.location} onChange={e => set('location',e.target.value)} placeholder="e.g. Mbabane Road near RFM Hospital" />
                    </div></Col>
                    <Col span={24}><div style={fbox}><div style={lbl}>Description <span style={{ color:'red' }}>*</span></div>
                        <TextArea rows={3} value={form.description} onChange={e => set('description',e.target.value)} />
                    </div></Col>
                    <Col span={24}><div style={fbox}><div style={lbl}>Injuries Reported</div>
                        <Input value={form.injuries_reported} onChange={e => set('injuries_reported',e.target.value)}
                            placeholder="e.g. None / Minor cuts / 2 hospitalised" />
                    </div></Col>
                    <Col span={12}><div style={fbox}><div style={lbl}>Third Party Involved?</div>
                        <Select style={{ width:'100%' }} value={form.third_party} onChange={v => set('third_party',v)}>
                            <Option value={0}>No</Option><Option value={1}>Yes</Option>
                        </Select>
                    </div></Col>
                    <Col span={12}><div style={fbox}><div style={lbl}>Third Party Details</div>
                        <Input value={form.third_party_details||''} onChange={e => set('third_party_details',e.target.value)}
                            disabled={!form.third_party} placeholder="Name, vehicle, contact" />
                    </div></Col>
                </Row>
            </div>

            {/* Damage & Recovery */}
            <div style={{ background:'#fffbe6', border:'1px solid #ffe58f', borderRadius:6, padding:'14px 16px', marginBottom:16 }}>
                <div style={{ fontWeight:600, color:'#d48806', marginBottom:12 }}>💰 Damage & Recovery</div>
                <Row gutter={16}>
                    <Col span={24}><div style={fbox}><div style={lbl}>Damage Assessment</div>
                        <TextArea rows={2} value={form.damage_assessment} onChange={e => set('damage_assessment',e.target.value)} />
                    </div></Col>
                    <Col span={8}><div style={fbox}><div style={lbl}>Est. Repair Cost (E)</div>
                        <Input type="number" value={form.repair_cost} onChange={e => set('repair_cost',e.target.value)} placeholder="0.00" />
                    </div></Col>
                    <Col span={8}><div style={fbox}><div style={lbl}>Police Report No.</div>
                        <Input value={form.police_report_no} onChange={e => set('police_report_no',e.target.value)} />
                    </div></Col>
                    <Col span={8}><div style={fbox}><div style={lbl}>Insurance Claim No.</div>
                        <Input value={form.insurance_claim_no} onChange={e => set('insurance_claim_no',e.target.value)} />
                    </div></Col>
                    <Col span={8}><div style={fbox}><div style={lbl}>Claim Status</div>
                        <Select style={{ width:'100%' }} value={form.claim_status} onChange={v => set('claim_status',v)}>
                            {CLAIM_STATUSES.map(s => <Option key={s} value={s}>{s}</Option>)}
                        </Select>
                    </div></Col>
                </Row>
            </div>

            {/* Resolution */}
            <div style={{ background:'#f6ffed', border:'1px solid #b7eb8f', borderRadius:6, padding:'14px 16px', marginBottom:20 }}>
                <div style={{ fontWeight:600, color:'#389e0d', marginBottom:12 }}>✅ Action & Resolution</div>
                <Row gutter={16}>
                    <Col span={24}><div style={fbox}><div style={lbl}>Action Taken</div>
                        <TextArea rows={2} value={form.action_taken} onChange={e => set('action_taken',e.target.value)} />
                    </div></Col>
                    <Col span={8}><div style={fbox}><div style={lbl}>Date Resolved</div>
                        <DatePicker style={{ width:'100%' }}
                            value={form.date_resolved ? moment(form.date_resolved) : null}
                            onChange={d => set('date_resolved', d ? d.format('YYYY-MM-DD') : null)}
                            format="DD MMM YYYY" placeholder="Leave blank if still open" />
                    </div></Col>
                    <Col span={24}><div style={fbox}><div style={lbl}>Notes</div>
                        <TextArea rows={2} value={form.notes} onChange={e => set('notes',e.target.value)} />
                    </div></Col>
                </Row>
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button type="primary" loading={saving} onClick={handleSave}
                    style={{ background:'#d4380d', borderColor:'#d4380d' }}>
                    {form.id ? 'Update Incident' : 'Log Incident'}
                </Button>
            </div>
        </Modal>
    );
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────
function IncidentDetail({ incident, onClose, onEdit }) {
    if (!incident) return null;
    const sev = SEV_C[incident.severity] || {};
    const row = (label, value) => (
        <div style={{ display:'flex', padding:'6px 0', borderBottom:'1px solid #f0f0f0' }}>
            <span style={{ width:170, fontSize:12, color:'#888', flexShrink:0 }}>{label}</span>
            <span style={{ fontSize:12, fontWeight:500 }}>{value||'—'}</span>
        </div>
    );
    return (
        <Modal open={!!incident} onCancel={onClose} width={620} destroyOnClose
            footer={
                <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
                    <Button onClick={onClose}>Close</Button>
                    <Button type="primary" icon={<EditOutlined />} onClick={() => { onClose(); onEdit(incident); }}>Edit</Button>
                </div>
            }
            title={
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <FileTextOutlined />
                    <span>Incident #{incident.id} — {incident.registration_no}</span>
                    <Tag color={STATUS_C[incident.status]||'default'}>{incident.status}</Tag>
                    {incident.severity && (
                        <Tag style={{ color:sev.text, background:sev.bg, borderColor:sev.border }}>{incident.severity}</Tag>
                    )}
                </div>
            }>
            {row('Vehicle',           `${incident.registration_no} — ${incident.vehicle_type}`)}
            {row('Driver',            incident.driver_name)}
            {row('Date & Time',       `${fmtD(incident.incident_date)} ${incident.incident_time||''}`.trim())}
            {row('Incident Type',     incident.incident_type)}
            {row('Region',            incident.region)}
            {row('Location',          incident.location)}
            {row('Facility',          incident.facility)}
            <div style={{ padding:'8px 0', borderBottom:'1px solid #f0f0f0' }}>
                <div style={{ fontSize:12, color:'#888', marginBottom:4 }}>Description</div>
                <div style={{ fontSize:12, background:'#fafafa', borderRadius:4, padding:8, whiteSpace:'pre-wrap' }}>
                    {incident.description||'—'}
                </div>
            </div>
            {row('Injuries',          incident.injuries_reported)}
            {row('Third Party',       incident.third_party==1?'Yes':'No')}
            {row('Damage Assessment', incident.damage_assessment)}
            {row('Est. Repair Cost',  incident.repair_cost ? `E ${toN(incident.repair_cost).toLocaleString()}` : null)}
            {row('Police Report No.', incident.police_report_no)}
            {row('Insurance Claim',   incident.insurance_claim_no)}
            {row('Claim Status',      incident.claim_status)}
            <div style={{ padding:'8px 0', borderBottom:'1px solid #f0f0f0' }}>
                <div style={{ fontSize:12, color:'#888', marginBottom:4 }}>Action Taken</div>
                <div style={{ fontSize:12, background:'#fafafa', borderRadius:4, padding:8, whiteSpace:'pre-wrap' }}>
                    {incident.action_taken||'—'}
                </div>
            </div>
            {row('Date Resolved',     fmtD(incident.date_resolved))}
            {row('Reported By',       incident.reported_by_name)}
            {incident.notes && row('Notes', incident.notes)}
        </Modal>
    );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function FleetIncidents() {
    const [incidents,    setIncidents]    = useState([]);
    const [vehicles,     setVehicles]     = useState([]);
    const [drivers,      setDrivers]      = useState([]);
    const [loading,      setLoading]      = useState(false);
    const [formOpen,     setFormOpen]     = useState(false);
    const [editing,      setEditing]      = useState(null);
    const [detailItem,   setDetailItem]   = useState(null);
    const [showDash,     setShowDash]     = useState(true);
    const [period,       setPeriod]       = useState(DEFAULT_PERIOD);
    const [search,       setSearch]       = useState('');
    const [filterType,   setFilterType]   = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterSev,    setFilterSev]    = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [iR, vR, dR] = await Promise.all([
                FleetService.getIncidents(),
                FleetService.getVehiclesList(),
                FleetService.getDriversList(),
            ]);
            if (Number(iR?.data?.code) === 200) setIncidents(iR.data.result || []);
            if (Number(vR?.data?.code) === 200) setVehicles(vR.data.result  || []);
            if (Number(dR?.data?.code) === 200) setDrivers(dR.data.result   || []);
        } catch { message.error('Failed to load incidents'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const handleDelete = async (id) => {
        try {
            const res = await FleetService.deleteIncident(id);
            if (Number(res?.data?.code) === 200) { message.success('Deleted'); load(); }
            else message.error(res?.data?.message||'Failed');
        } catch { message.error('Failed'); }
    };

    const stats = {
        total:      incidents.length,
        open:       incidents.filter(i => i.status==='Open').length,
        resolved:   incidents.filter(i => i.status==='Resolved').length,
        high:       incidents.filter(i => ['High','Critical'].includes(i.severity)).length,
        this_month: incidents.filter(i => moment(i.incident_date).isSame(moment(),'month')).length,
    };

    const filtered = incidents.filter(i => {
        const q = search.toLowerCase();
        const ms = !q || (i.registration_no||'').toLowerCase().includes(q)
                      || (i.incident_type||'').toLowerCase().includes(q)
                      || (i.location||'').toLowerCase().includes(q)
                      || (i.driver_name||'').toLowerCase().includes(q);
        return ms && (!filterType||i.incident_type===filterType)
                  && (!filterStatus||i.status===filterStatus)
                  && (!filterSev||i.severity===filterSev);
    });

    const anomalyCount = useMemo(() => detectIncidentAnomalies(incidents).length, [incidents]);

    const SevBadge = ({ s }) => {
        const c = SEV_C[s]||{};
        return s ? <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:10,
            background:c.bg, border:`1px solid ${c.border}`, color:c.text }}>{s}</span> : null;
    };

    return (
        <div className="page-content">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <div>
                    <h4 style={{ margin:0, fontWeight:700 }}>Fleet Incident Log</h4>
                    <div style={{ fontSize:13, color:'#888', marginTop:2 }}>
                        Record and manage all vehicle incidents, accidents, and breakdowns
                    </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                    <Button icon={<DashboardOutlined />}
                        type={showDash?'primary':'default'} ghost={showDash}
                        onClick={() => setShowDash(p=>!p)}>
                        {showDash ? 'Hide Dashboard' : 'Show Dashboard'}
                        {anomalyCount > 0 && !showDash && (
                            <span style={{ marginLeft:6, background:'#f5222d', color:'#fff',
                                borderRadius:8, padding:'0 5px', fontSize:10 }}>{anomalyCount}</span>
                        )}
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />}
                        onClick={() => { setEditing(null); setFormOpen(true); }}
                        style={{ background:'#d4380d', borderColor:'#d4380d' }}>
                        Log Incident
                    </Button>
                </div>
            </div>

            {/* KPI Cards */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:20 }}>
                {[
                    { label:'Total Incidents', value:stats.total,      color:'#595959' },
                    { label:'Open',            value:stats.open,       color:'#d46b08' },
                    { label:'Resolved',        value:stats.resolved,   color:'#389e0d' },
                    { label:'High / Critical', value:stats.high,       color:'#c41d7f' },
                    { label:'This Month',      value:stats.this_month, color:'#2f54eb' },
                ].map(({ label,value,color }) => (
                    <div key={label} style={{ background:'#fff', border:'1px solid #e8e8e8',
                        borderRadius:8, padding:'14px 16px', borderTop:`3px solid ${color}` }}>
                        <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>{label}</div>
                        <div style={{ fontSize:26, fontWeight:700, color }}>{value}</div>
                    </div>
                ))}
            </div>

            {/* Mini Dashboard */}
            {showDash && incidents.length > 0 && (
                <IncidentDashboard incidents={filterByPeriod(incidents, period, 'incident_date')} period={period} onPeriodChange={setPeriod} />
            )}

            {/* Filters */}
            <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap',
                background:'#fafafa', border:'1px solid #e8e8e8', borderRadius:6, padding:'12px 14px' }}>
                <Input prefix={<SearchOutlined style={{ color:'#ccc' }} />}
                    placeholder="Search vehicle, type, location, driver…"
                    value={search} onChange={e => setSearch(e.target.value)}
                    allowClear style={{ width:260 }} />
                <Select placeholder="Incident Type" value={filterType||undefined}
                    onChange={setFilterType} allowClear style={{ width:180 }}>
                    {INCIDENT_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                </Select>
                <Select placeholder="Status" value={filterStatus||undefined}
                    onChange={setFilterStatus} allowClear style={{ width:130 }}>
                    <Option value="Open">Open</Option>
                    <Option value="Resolved">Resolved</Option>
                    <Option value="Under Review">Under Review</Option>
                </Select>
                <Select placeholder="Severity" value={filterSev||undefined}
                    onChange={setFilterSev} allowClear style={{ width:130 }}>
                    {SEVERITIES.map(s => <Option key={s} value={s}>{s}</Option>)}
                </Select>
                <div style={{ marginLeft:'auto', fontSize:12, color:'#888', alignSelf:'center' }}>
                    {filtered.length} of {incidents.length}
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div style={{ textAlign:'center', padding:60, color:'#aaa' }}>Loading…</div>
            ) : filtered.length === 0 ? (
                <div style={{ textAlign:'center', padding:60, color:'#aaa',
                    border:'2px dashed #e8e8e8', borderRadius:8 }}>
                    <ExclamationCircleOutlined style={{ fontSize:40, marginBottom:12, display:'block' }} />
                    {incidents.length === 0 ? 'No incidents logged yet.' : 'No records match your filters.'}
                </div>
            ) : (
                <div style={{ border:'1px solid #e8e8e8', borderRadius:8, overflow:'hidden' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                        <thead>
                            <tr style={{ background:'#1a5b9a', color:'#fff' }}>
                                {['Date','Vehicle','Driver','Type','Location','Severity','Damage (E)','Status','Claim','Actions'].map(h => (
                                    <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontWeight:600, fontSize:11, whiteSpace:'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((inc, idx) => (
                                <tr key={inc.id}
                                    style={{ background:idx%2===0?'#fff':'#f9f9f9',
                                        borderBottom:'1px solid #f0f0f0', cursor:'pointer' }}
                                    onClick={() => setDetailItem(inc)}>
                                    <td style={{ padding:'10px 12px', whiteSpace:'nowrap' }}>
                                        <div style={{ fontWeight:600 }}>{fmtD(inc.incident_date)}</div>
                                        {inc.incident_time && <div style={{ color:'#888', fontSize:11 }}>{inc.incident_time}</div>}
                                    </td>
                                    <td style={{ padding:'10px 12px' }}>
                                        <div style={{ fontWeight:600, color:'#1a5b9a' }}>{fmt(inc.registration_no)}</div>
                                        <div style={{ color:'#888', fontSize:11 }}>{fmt(inc.vehicle_type)}</div>
                                    </td>
                                    <td style={{ padding:'10px 12px' }}>{fmt(inc.driver_name)}</td>
                                    <td style={{ padding:'10px 12px' }}><Tag style={{ fontSize:11 }}>{inc.incident_type}</Tag></td>
                                    <td style={{ padding:'10px 12px', maxWidth:140 }}>
                                        <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{fmt(inc.location)}</div>
                                        {inc.region && <div style={{ color:'#888', fontSize:11 }}>{inc.region}</div>}
                                    </td>
                                    <td style={{ padding:'10px 12px' }}><SevBadge s={inc.severity} /></td>
                                    <td style={{ padding:'10px 12px', textAlign:'right' }}>
                                        {inc.repair_cost ? `E ${toN(inc.repair_cost).toLocaleString()}` : '—'}
                                    </td>
                                    <td style={{ padding:'10px 12px' }}>
                                        <Tag color={STATUS_C[inc.status]||'default'}>{inc.status||'Open'}</Tag>
                                    </td>
                                    <td style={{ padding:'10px 12px' }}>
                                        {inc.claim_status && inc.claim_status !== 'Not Applicable'
                                            ? <Tag style={{ fontSize:11 }}>{inc.claim_status}</Tag>
                                            : <span style={{ color:'#ccc' }}>—</span>}
                                    </td>
                                    <td style={{ padding:'10px 12px' }} onClick={e => e.stopPropagation()}>
                                        <div style={{ display:'flex', gap:6 }}>
                                            <Tooltip title="Edit">
                                                <Button size="small" icon={<EditOutlined />}
                                                    onClick={() => { setEditing(inc); setFormOpen(true); }} />
                                            </Tooltip>
                                            <Popconfirm title="Delete this incident?"
                                                onConfirm={() => handleDelete(inc.id)}
                                                okText="Delete" okButtonProps={{ danger:true }}>
                                                <Button size="small" danger icon={<DeleteOutlined />} />
                                            </Popconfirm>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <IncidentFormModal open={formOpen} incident={editing} vehicles={vehicles} drivers={drivers}
                onSaved={() => { setFormOpen(false); load(); }} onClose={() => setFormOpen(false)} />
            <IncidentDetail incident={detailItem} onClose={() => setDetailItem(null)}
                onEdit={inc => { setDetailItem(null); setEditing(inc); setFormOpen(true); }} />
        </div>
    );
}